import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { PaperPlaneTilt, PushPin, Trash, ChatCircle, Warning, Bell as BellIcon } from '@phosphor-icons/react';

const CATEGORY_STYLES = {
  general: { bg: 'bg-white/5', border: 'border-white/10', label: 'General' },
  urgent: { bg: 'bg-red-500/10', border: 'border-red-500/20', label: 'Urgent' },
  reminder: { bg: 'bg-[#FF6B35]/10', border: 'border-[#FF6B35]/20', label: 'Reminder' }
};

export default function FamilyChatPage() {
  const { api, user } = useAuth();
  const { t } = useLanguage();
  const [messages, setMessages] = useState([]);
  const [pinned, setPinned] = useState([]);
  const [input, setInput] = useState('');
  const [category, setCategory] = useState('general');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const pollRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const [msgRes, pinRes] = await Promise.all([
        api.get('/chat/messages?limit=100'),
        api.get('/chat/pinned')
      ]);
      setMessages(msgRes.data);
      setPinned(pinRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api]);

  useEffect(() => {
    fetchMessages();
    pollRef.current = setInterval(fetchMessages, 5000);
    return () => clearInterval(pollRef.current);
  }, [fetchMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    setSending(true);
    try {
      await api.post('/chat/messages', { text: input.trim(), category });
      setInput('');
      setCategory('general');
      await fetchMessages();
    } catch (err) { console.error(err); }
    setSending(false);
  };

  const togglePin = async (id) => {
    try {
      await api.put(`/chat/messages/${id}/pin`);
      fetchMessages();
    } catch (err) { console.error(err); }
  };

  const deleteMsg = async (id) => {
    try {
      await api.delete(`/chat/messages/${id}`);
      fetchMessages();
    } catch (err) { console.error(err); }
  };

  const isAdmin = user?.role === 'admin';

  return (
    <div data-testid="family-chat-page" className="flex flex-col h-[calc(100vh-7rem)] md:h-[calc(100vh-5rem)]">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="overline">Family</p>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-white tracking-tight mt-1">Chat & Announcements</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-[#22C55E] status-pulse" />
          <span className="text-xs text-neutral-500">{messages.length} messages</span>
        </div>
      </div>

      {/* Pinned Announcements */}
      {pinned.length > 0 && (
        <div className="mb-4 space-y-2" data-testid="pinned-messages">
          {pinned.map(p => (
            <div key={p.message_id} className="glass-card px-4 py-3 flex items-start gap-3 border-[#FF6B35]/20">
              <PushPin size={16} weight="fill" className="text-[#FF6B35] mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white">{p.text}</p>
                <p className="text-[10px] text-neutral-500 mt-1">{p.user_name} &middot; {new Date(p.created_at).toLocaleDateString()}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-1 pr-2 pb-2" data-testid="chat-messages">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-6 h-6 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <ChatCircle size={48} weight="duotone" className="text-neutral-600 mb-4" />
            <p className="text-neutral-500 text-sm">No messages yet. Start the conversation!</p>
          </div>
        ) : messages.map(msg => {
          const isMine = msg.user_id === user?.user_id;
          const catStyle = CATEGORY_STYLES[msg.category] || CATEGORY_STYLES.general;
          return (
            <div key={msg.message_id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`} data-testid={`chat-msg-${msg.message_id}`}>
              <div className={`max-w-[80%] md:max-w-[60%] group ${isMine ? 'ml-auto' : ''}`}>
                <div className={`px-4 py-3 rounded-2xl ${isMine ? 'bg-[#FF6B35]/15 border border-[#FF6B35]/20 rounded-br-lg' : `${catStyle.bg} border ${catStyle.border} rounded-bl-lg`}`}>
                  {!isMine && <p className="text-[10px] font-semibold text-[#FF6B35] mb-1">{msg.user_name}</p>}
                  {msg.category !== 'general' && (
                    <span className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded mb-1.5
                      ${msg.category === 'urgent' ? 'bg-red-500/20 text-red-400' : 'bg-[#FF6B35]/20 text-[#FF6B35]'}`}>
                      {msg.category === 'urgent' ? <Warning size={8} /> : <BellIcon size={8} />}
                      {catStyle.label}
                    </span>
                  )}
                  <p className="text-sm text-neutral-200 leading-relaxed">{msg.text}</p>
                  <div className="flex items-center justify-between mt-1.5 gap-3">
                    <span className="text-[10px] text-neutral-600">
                      {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {isAdmin && (
                        <button onClick={() => togglePin(msg.message_id)} title={msg.pinned ? 'Unpin' : 'Pin'}
                          className={`p-1 rounded hover:bg-white/10 ${msg.pinned ? 'text-[#FF6B35]' : 'text-neutral-500'}`}>
                          <PushPin size={12} weight={msg.pinned ? 'fill' : 'regular'} />
                        </button>
                      )}
                      {(isMine || isAdmin) && (
                        <button onClick={() => deleteMsg(msg.message_id)} title="Delete"
                          className="p-1 rounded hover:bg-white/10 text-neutral-500 hover:text-red-400">
                          <Trash size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <form onSubmit={sendMessage} className="mt-3 flex items-end gap-3" data-testid="chat-input-form">
        <div className="flex-1 glass-card px-4 py-3 flex items-center gap-3">
          <select value={category} onChange={e => setCategory(e.target.value)} data-testid="chat-category-select"
            className="bg-transparent text-xs text-neutral-500 outline-none border-none pr-1 cursor-pointer">
            <option value="general">General</option>
            <option value="urgent">Urgent</option>
            <option value="reminder">Reminder</option>
          </select>
          <div className="w-px h-5 bg-white/10" />
          <input value={input} onChange={e => setInput(e.target.value)} placeholder="Type a message..."
            data-testid="chat-input"
            className="flex-1 bg-transparent text-sm text-white placeholder-neutral-600 outline-none" />
        </div>
        <button type="submit" disabled={sending || !input.trim()} data-testid="chat-send-btn"
          className="w-12 h-12 rounded-2xl bg-[#FF6B35] text-white btn-glow flex items-center justify-center disabled:opacity-40 flex-shrink-0">
          {sending ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <PaperPlaneTilt size={20} weight="fill" />}
        </button>
      </form>
    </div>
  );
}
